import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Theme, ThemesStore, ThemeInput, ThemeExport } from '../types'
import { BLOOD_MOON_THEME } from '../data/defaultThemes'
import { themesApi } from '../api/themesApi'
import { useDebouncedCallback } from './useDebouncedCallback'

const STORAGE_KEY = 'nimble-themes-v1'

// Detect authentication from data attribute set in ERB
function isAuthenticated(): boolean {
  const el = document.getElementById('themes')
  return el?.dataset.authenticated === 'true'
}

// Generate a unique ID
function generateId(): string {
  return `theme-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

// Generate a URL-friendly slug from a name
function generateSlug(name: string, existingSlugs: string[] = []): string {
  let slug = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  if (!slug) slug = 'theme'
  // Deduplicate
  let finalSlug = slug
  let counter = 2
  while (existingSlugs.includes(finalSlug)) {
    finalSlug = `${slug}-${counter}`
    counter++
  }
  return finalSlug
}

// Get current ISO timestamp
function getTimestamp(): string {
  return new Date().toISOString()
}

// Load from localStorage
function loadStore(): ThemesStore {
  if (typeof window === 'undefined') {
    return { themes: [], activeThemeId: null, prefersDarkMode: true }
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed: ThemesStore = JSON.parse(stored)
      // Backfill slugs for existing themes that don't have one
      const slugs: string[] = []
      let changed = false
      for (const theme of parsed.themes) {
        if (!theme.slug) {
          theme.slug = generateSlug(theme.name, slugs)
          changed = true
        }
        slugs.push(theme.slug)
      }
      if (changed) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed))
      }
      return parsed
    }
  } catch {
    // Ignore parse errors
  }

  // Initialize with Blood Moon theme on first load
  const timestamp = getTimestamp()
  const bloodMoon: Theme = {
    ...BLOOD_MOON_THEME,
    id: generateId(),
    slug: generateSlug(BLOOD_MOON_THEME.name),
    createdAt: timestamp,
    updatedAt: timestamp,
  }

  return {
    themes: [bloodMoon],
    activeThemeId: bloodMoon.id,
    prefersDarkMode: true,
  }
}

// Save to localStorage
function saveStore(store: ThemesStore): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
}

export function useThemes() {
  const [store, setStore] = useState<ThemesStore>(loadStore)
  const [loading, setLoading] = useState(true)
  const authenticated = useRef(isAuthenticated())
  const initializedRef = useRef(false)

  // Persist to localStorage when store changes
  useEffect(() => {
    saveStore(store)
  }, [store])

  // Fetch themes from API on mount
  useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    themesApi
      .list()
      .then((apiThemes) => {
        setStore((prev) => {
          if (authenticated.current) {
            // Authenticated: API themes are source of truth
            return {
              ...prev,
              themes: apiThemes,
              activeThemeId:
                apiThemes.find((t) => t.id === prev.activeThemeId)?.id ??
                apiThemes[0]?.id ??
                null,
            }
          } else {
            // Unauthenticated: merge system themes from API into local themes
            const localIds = new Set(prev.themes.map((t) => t.slug))
            const systemThemes = apiThemes.filter(
              (t) => t.systemTheme && !localIds.has(t.slug)
            )
            const merged = [...systemThemes, ...prev.themes]
            return {
              ...prev,
              themes: merged,
              activeThemeId: prev.activeThemeId ?? merged[0]?.id ?? null,
            }
          }
        })
      })
      .catch(() => {
        // API unavailable — keep localStorage themes
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  // Get the active theme
  const activeTheme = useMemo(() => {
    if (!store.activeThemeId) return null
    return store.themes.find((t) => t.id === store.activeThemeId) ?? null
  }, [store.themes, store.activeThemeId])

  // Get current color scheme based on mode preference
  const currentColors = useMemo(() => {
    if (!activeTheme) return null
    return store.prefersDarkMode ? activeTheme.darkMode : activeTheme.lightMode
  }, [activeTheme, store.prefersDarkMode])

  // Debounced API PATCH for theme updates
  const debouncedPatch = useDebouncedCallback(
    (slug: string, updates: Partial<ThemeInput>) => {
      themesApi.update(slug, updates).catch(() => {
        // Silently fail — optimistic update already applied
      })
    },
    500
  )

  // Create a new theme
  const createTheme = useCallback(
    async (input: ThemeInput): Promise<Theme> => {
      if (authenticated.current) {
        const created = await themesApi.create(input)
        setStore((prev) => ({
          ...prev,
          themes: [...prev.themes, created],
          activeThemeId: created.id,
        }))
        return created
      }

      // Unauthenticated: localStorage only
      const timestamp = getTimestamp()
      const existingSlugs = store.themes.map((t) => t.slug)
      const newTheme: Theme = {
        ...input,
        id: generateId(),
        slug: generateSlug(input.name, existingSlugs),
        createdAt: timestamp,
        updatedAt: timestamp,
      }

      setStore((prev) => ({
        ...prev,
        themes: [...prev.themes, newTheme],
        activeThemeId: newTheme.id,
      }))

      return newTheme
    },
    [store.themes]
  )

  // Delete a theme
  const deleteTheme = useCallback(
    (id: string) => {
      const theme = store.themes.find((t) => t.id === id)

      // Optimistic local delete
      setStore((prev) => {
        const filtered = prev.themes.filter((t) => t.id !== id)
        let newActiveId = prev.activeThemeId
        if (prev.activeThemeId === id) {
          newActiveId = filtered.length > 0 ? filtered[0].id : null
        }
        return { ...prev, themes: filtered, activeThemeId: newActiveId }
      })

      // Fire-and-forget API delete
      if (authenticated.current && theme?.slug) {
        themesApi.destroy(theme.slug).catch(() => {})
      }
    },
    [store.themes]
  )

  // Update a theme
  const updateTheme = useCallback(
    (id: string, updates: Partial<ThemeInput>) => {
      // Optimistic local update
      setStore((prev) => ({
        ...prev,
        themes: prev.themes.map((t) =>
          t.id === id ? { ...t, ...updates, updatedAt: getTimestamp() } : t
        ),
      }))

      // Debounced API PATCH for authenticated users
      if (authenticated.current) {
        const theme = store.themes.find((t) => t.id === id)
        if (theme?.slug) {
          debouncedPatch(theme.slug, updates)
        }
      }
    },
    [store.themes, debouncedPatch]
  )

  // Set the active theme
  const setActiveTheme = useCallback((id: string) => {
    setStore((prev) => ({
      ...prev,
      activeThemeId: id,
    }))
  }, [])

  // Toggle dark/light mode
  const toggleDarkMode = useCallback(() => {
    setStore((prev) => ({
      ...prev,
      prefersDarkMode: !prev.prefersDarkMode,
    }))
  }, [])

  // Set dark mode explicitly
  const setDarkMode = useCallback((isDark: boolean) => {
    setStore((prev) => ({
      ...prev,
      prefersDarkMode: isDark,
    }))
  }, [])

  // Duplicate a theme
  const duplicateTheme = useCallback(
    async (id: string): Promise<Theme | null> => {
      const theme = store.themes.find((t) => t.id === id)
      if (!theme) return null

      if (authenticated.current && theme.slug) {
        const duplicated = await themesApi.duplicate(theme.slug)
        setStore((prev) => ({
          ...prev,
          themes: [...prev.themes, duplicated],
          activeThemeId: duplicated.id,
        }))
        return duplicated
      }

      // Unauthenticated: localStorage only
      const timestamp = getTimestamp()
      const existingSlugs = store.themes.map((t) => t.slug)
      const copyName = `${theme.name} (Copy)`
      const newTheme: Theme = {
        ...JSON.parse(JSON.stringify(theme)),
        id: generateId(),
        slug: generateSlug(copyName, existingSlugs),
        name: copyName,
        createdAt: timestamp,
        updatedAt: timestamp,
      }

      setStore((prev) => ({
        ...prev,
        themes: [...prev.themes, newTheme],
        activeThemeId: newTheme.id,
      }))

      return newTheme
    },
    [store.themes]
  )

  // Export a theme as JSON
  const exportTheme = useCallback(
    (id: string): ThemeExport | null => {
      const theme = store.themes.find((t) => t.id === id)
      if (!theme) return null

      return {
        version: '1.0',
        exportedAt: getTimestamp(),
        theme,
      }
    },
    [store.themes]
  )

  // Import a theme from JSON
  const importTheme = useCallback(
    async (exported: ThemeExport): Promise<Theme> => {
      const importName = `${exported.theme.name} (Imported)`

      if (authenticated.current) {
        // Strip metadata, send as create
        const { id, createdAt, updatedAt, systemTheme, userId, slug, ...rest } =
          exported.theme
        const created = await themesApi.create({ ...rest, name: importName })
        setStore((prev) => ({
          ...prev,
          themes: [...prev.themes, created],
          activeThemeId: created.id,
        }))
        return created
      }

      // Unauthenticated: localStorage only
      const timestamp = getTimestamp()
      const existingSlugs = store.themes.map((t) => t.slug)
      const newTheme: Theme = {
        ...exported.theme,
        id: generateId(),
        slug: generateSlug(importName, existingSlugs),
        name: importName,
        createdAt: timestamp,
        updatedAt: timestamp,
      }

      setStore((prev) => ({
        ...prev,
        themes: [...prev.themes, newTheme],
        activeThemeId: newTheme.id,
      }))

      return newTheme
    },
    [store.themes]
  )

  // Look up a theme by slug
  const getThemeBySlug = useCallback(
    (slug: string): Theme | null => {
      return store.themes.find((t) => t.slug === slug) ?? null
    },
    [store.themes]
  )

  return {
    themes: store.themes,
    activeTheme,
    activeThemeId: store.activeThemeId,
    prefersDarkMode: store.prefersDarkMode,
    currentColors,
    loading,
    createTheme,
    deleteTheme,
    updateTheme,
    setActiveTheme,
    toggleDarkMode,
    setDarkMode,
    duplicateTheme,
    exportTheme,
    importTheme,
    getThemeBySlug,
    hasNoThemes: store.themes.length === 0,
  }
}
